import type { Floor } from '@/content/floors';
import { strings } from '@/content/strings';
import { letterCount } from '@/game/codename';
import type { FloorProgress } from '@/game/progress';
import { cssVars } from '@/lib/cssVars';
import { cx } from '@/lib/cx';
import { CodenameTiles } from '@/ui/CodenameTiles';
import { Icon } from '@/ui/Icon';
import { StarRating } from '@/ui/StarRating';
import styles from './FloorCard.module.css';

interface FloorCardProps {
  floor: Floor;
  progress: FloorProgress;
  locked: boolean;
  /** Where the elevator cab is (or is riding to). */
  current: boolean;
  /** Floor whose stars open this one (for a locked floor). */
  unlockFrom: number;
  onSelect: () => void;
}

/**
 * One floor of the Foe Elevator: floor number, the foe (a silhouette until unlocked), the
 * codename with the letters uncovered so far, the practised sound context and the stars.
 */
export function FloorCard({
  floor,
  progress,
  locked,
  current,
  unlockFrom,
  onSelect,
}: FloorCardProps) {
  const t = strings.elevator;
  const label = locked
    ? t.lockedLabel(floor.number, unlockFrom)
    : t.floorLabel(floor.number, floor.focus, progress.stars);
  return (
    <button
      type="button"
      className={cx(styles.card, locked && styles.locked, current && styles.current)}
      style={cssVars({
        '--floor-color': floor.color,
        '--fit': Math.min(1, 10 / letterCount(floor.foe.codename)),
        // Width of the codename in ems: letter tiles plus the gaps between words.
        '--codename-em':
          letterCount(floor.foe.codename) * 1.08 +
          (floor.foe.codename.trim().split(/\s+/).length - 1) * 0.5,
      })}
      onClick={onSelect}
      aria-disabled={locked}
      aria-current={current ? 'true' : undefined}
      aria-label={label}
    >
      <span className={styles.number} aria-hidden>
        {floor.number}
      </span>
      <span className={styles.portrait} aria-hidden>
        <img
          src={floor.foe.portraits.disguised.url}
          alt=""
          draggable={false}
          className={cx(styles.foe, locked && styles.silhouette)}
        />
        {locked && <span className={styles.question}>?</span>}
      </span>
      <span className={styles.info} aria-hidden>
        <CodenameTiles
          codename={floor.foe.codename}
          revealedCount={locked ? 0 : progress.revealed}
          className={cx(styles.codename)}
        />
        <span className={styles.meta}>
          <span className={styles.focus}>{floor.focus}</span>
          {locked ? (
            <span className={styles.need}>
              <Icon name="lock" className={styles.lock} />
              <span>{t.needs(unlockFrom)}</span>
            </span>
          ) : (
            <StarRating stars={progress.stars} size={16} />
          )}
        </span>
      </span>
    </button>
  );
}
